import { PageHeader } from 'components/layout';
import { Button, Icon } from 'design-system-web';
import { Thumbnail } from 'components/general';
import { H5 } from 'components/typography';
import { routes } from 'lane-shared/config';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import styles from './styles.scss';
import { TenantCSVType } from './types';
import { Space, useFlatfile, useListener, Workbook } from '@flatfile/react';
import { workbook } from './flatfile/workbook';
import { listener } from './flatfile/listener';
import { FlatfileWrapper } from './FlatfileWrapper';
import {
  uploadCSVMutation,
  createAutoSetupConfiguration,
} from 'graphql-queries';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import Papa from 'papaparse';
import { makeFileDownload } from 'helpers';

type Props = {
  channelId: string;
};

export const AddTenantsBulk = ({ channelId }: Props) => {
  return (
    <FlatfileWrapper>
      <AddTenantsBulkContent channelId={channelId} />
    </FlatfileWrapper>
  );
};

const AddTenantsBulkContent = ({ channelId }: Props) => {
  const [tenants, setTenants] = useState<TenantCSVType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const { open, openPortal, closePortal } = useFlatfile();
  const { channel } = useChannelAdminContext();

  const [uploadCSV] = useMutation(uploadCSVMutation);
  const [createAutoSetup] = useMutation(createAutoSetupConfiguration);

  const path = {
    defaultPath: routes.channelAdminRelationships.replace(':id', channelId),
  };

  const onCancel = () => {
    history.push(path.defaultPath);
  };

  const handleAddTenants = async () => {
    if (tenants.length === 0) return;

    setLoading(true);

    try {
      const csvText = convertTenantsToCSV(tenants);
      const fileName = `tenants-${new Date().toISOString().split('T')[0]}.csv`;

      const { data } = await uploadCSV({
        variables: {
          uploadCSVData: {
            text: csvText,
            contentType: 'text/csv',
            fileName,
          },
        },
      });

      if (data?.uploadCSV?.fileUrl) {
        await createAutoSetup({
          variables: {
            autoSetupData: {
              source: 'csv',
              sourceLocations: [data.uploadCSV.fileUrl],
              parentChannelName: channel?.name,
              parentChannelId: channel?._id,
              tenantsOnly: true,
              newTenantImport: true,
            },
          },
        });

        window.Toast.show(
          <p>
            {t(
              'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadSuccessToast'
            )}
          </p>
        );

        history.push(path.defaultPath);
      }
    } catch (error: any) {
      window.Alert.show({
        title: t(
          'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadError.title'
        ),
        message: t(
          'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadError.message'
        ),
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = (importedTenants: TenantCSVType[]) => {
    setTenants(importedTenants);
  };

  const handleDeleteTenants = () => {
    setTenants([]);
    setIsDeleting(false);
  };

  const convertTenantsToCSV = (tenants: TenantCSVType[]): string => {
    if (tenants.length === 0) return '';

    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }

      return value;
    };

    const csvRows = [
      csvTemplateColumns.join(','),
      ...tenants.map(tenant =>
        [
          escapeCSV(tenant.name || ''),
          escapeCSV(tenant.displayName || tenant.name),
          escapeCSV(tenant.tenantType || ''),
          escapeCSV(tenant.suites || ''),
          escapeCSV(tenant.externalLeaseId || ''),
          escapeCSV(tenant.sourceType || ''),
          escapeCSV(tenant.whiteListedEmailDomains || ''),
        ].join(',')
      ),
    ];

    return csvRows.join('\n');
  };

  const csvTemplateColumns = [
    'Tenant Name',
    'Tenant Display Name',
    'Tenant Type',
    'Suites',
    'External Lease ID',
    'External Source Type',
    'White Listed Email Domains',
  ];

  const downloadSampleCSV = () => {
    const csvData = [];
    const headers = csvTemplateColumns.map(column => column);

    csvData.push(headers);

    const exampleRow = [
      'Example Tenant Inc.',
      'Example Tenant',
      'Company',
      'Suite 101, Suite 102, Suite 103',
      'LEASE-12345, LEASE-12346, LEASE-12347',
      'CBRE, CBRE, MRI',
      'example.com, tenant.com',
    ];

    csvData.push(exampleRow);

    const csv = Papa.unparse(csvData);
    const filename = `add-tenants-template.csv`;

    makeFileDownload({
      name: filename,
      contents: csv,
      type: 'application/csv',
    });
  };

  useListener(listener(handleBulkAdd, () => closePortal()));

  const toggleFlatfilePortal = () => {
    if (!open) {
      openPortal();
    } else {
      closePortal();
    }
  };

  const pageHeaderProps = {
    header: t('web.pages.portal.admin.channel.relationships.addTenants.header'),
    breadcrumbs: [
      {
        label: t(
          'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.header'
        ),
        url: path.defaultPath,
      },
      {
        label: t(
          'web.pages.portal.admin.channel.relationships.addTenants.header'
        ),
      },
    ],
  };

  return (
    <div className={styles.outerWrapper}>
      <PageHeader {...pageHeaderProps} headerLevel="h3" />
      <div className={styles.cardContainer}>
        <div className={styles.cards}>
          <div className={styles.flatfileContainer}>
            <h4>
              {t(
                'web.pages.portal.admin.channel.relationships.addTenants.subtitle'
              )}
            </h4>
            <p>
              You can upload a CSV file or manually enter tenant information.
            </p>
            <div className={styles.buttonGroup}>
              <Button
                testId="openFlatfilePortal"
                variant="primary"
                size="large"
                onClick={toggleFlatfilePortal}
                disabled={loading}
              >
                {t(
                  'web.admin.channel.teamManagement.team.BulkSendInviteV2.inviteUsers.uploadCsvButtonText'
                )}
              </Button>
              <Button
                testId="downloadSampleCSV"
                variant="secondary"
                size="large"
                onClick={downloadSampleCSV}
                disabled={loading}
                startIcon={
                  <Icon
                    name="download"
                    testId="downloadIcon"
                    set="FontAwesome"
                  />
                }
              >
                Download Sample CSV
              </Button>
            </div>

            {tenants.length > 0 && (
              <div className={styles.tenantPreview}>
                <div className={styles.tenantListItem}>
                  <div className={styles.user}>
                    <Thumbnail
                      className={styles.thumbnail}
                      name={tenants.length.toString()}
                      text
                    />
                    <div className={styles.textWrapper}>
                      <H5>
                        {tenants.length === 1
                          ? 'Imported Tenant (1)'
                          : `Imported Tenants (${tenants.length})`}
                      </H5>
                    </div>
                  </div>
                  {!isDeleting && (
                    <div>
                      <Button
                        testId="deleteBulkButtonTrigger"
                        className={styles.button}
                        onClick={() => setIsDeleting(true)}
                        startIcon={
                          <Icon
                            name="trash-alt"
                            testId="deleteIcon"
                            set="FontAwesome"
                          />
                        }
                      >
                        {t('Remove')}
                      </Button>
                    </div>
                  )}
                  {isDeleting && (
                    <>
                      <div>
                        <Button
                          testId="deleteBulkButton"
                          className={styles.button}
                          onClick={handleDeleteTenants}
                        >
                          {t(
                            'web.content.feature.visitorManagement.visitorInvite.form.remove'
                          )}
                        </Button>
                      </div>
                      <div>
                        <Button
                          testId="cancelDeleteBulkButton"
                          className={styles.button}
                          onClick={() => setIsDeleting(false)}
                        >
                          {t(
                            'web.content.feature.visitorManagement.visitorInvite.form.cancel'
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actionButtonsBottom}>
          <Button
            testId="submitTenants"
            variant="primary"
            size="large"
            className={styles.button}
            loading={loading}
            disabled={tenants.length === 0 || loading}
            onClick={handleAddTenants}
          >
            {t(
              'web.pages.portal.admin.channel.relationships.BulkSendInvite.button.addTenants'
            )}
          </Button>

          <Button
            className={styles.button}
            variant="tertiary"
            size="large"
            onClick={onCancel}
            disabled={loading}
          >
            {t(
              'web.admin.channel.teamManagement.team.BulkSendInviteV2.clearInvitesButtonText'
            )}
          </Button>
        </div>
      </div>

      <Space
        config={{
          name: 'Activate Tenants Space',
          metadata: {
            sidebarConfig: {
              showSidebar: true,
            },
          },
        }}
      >
        <Workbook config={workbook} />
      </Space>
    </div>
  );
};
